import React, { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import CosmicOceanShader from './components/CosmicOceanShader';

// ── Helpers ──────────────────────────────────────────────────────────────────
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Data ─────────────────────────────────────────────────────────────────────
const services = [
  {
    icon: '💧',
    title: 'Su Tesisatı',
    desc: 'Boru montajı, sızıntı tamiri, su hattı yenileme. Her çaplı projeye eksiksiz çözüm.',
    detail: 'PPR, PPRC ve bakır boru sistemleriyle konut ve işyerlerinde eksiksiz su tesisatı kurulumu gerçekleştiriyoruz. Basınç testi ve garanti belgesiyle teslim ediyoruz.',
    features: ['Boru Montajı & Yenileme', 'Sızıntı Tespiti & Tamiri', 'Su Hattı Revizyonu', 'Basınç Testi'],
  },
  {
    icon: '🔥',
    title: 'Doğalgaz Tesisatı',
    desc: 'Lisanslı ustalarımızla güvenli gaz hattı kurulum ve bakım hizmetleri.',
    detail: 'EPDK lisanslı ustalarımızla doğalgaz hattı kurulum, değişim ve periyodik bakım hizmetleri sunuyoruz. Tüm işlemler resmi belge ve test raporuyla tamamlanır.',
    features: ['Gaz Hattı Kurulumu', 'Periyodik Bakım', 'Kaçak Tespiti', 'Resmi Belgelendirme'],
  },
  {
    icon: '🚿',
    title: 'Banyo & Mutfak',
    desc: 'Kombi, batarya, lavabo, duşakabin montajı. Sıfırdan yenileme projeleri.',
    detail: 'Banyo ve mutfaklarınızı sıfırdan tasarlıyor, tüm tesisat işlemlerini kendi ekibimizle tamamlıyoruz. Ürün temini ve montajı tek elden.',
    features: ['Duşakabin Montajı', 'Batarya & Lavabo', 'Kombi Kurulumu', 'Komple Yenileme'],
  },
  {
    icon: '🏗️',
    title: 'Pis Su Hattı',
    desc: 'Tıkanıklık açma, kanal temizliği ve atık su sistemi revizyonu.',
    detail: 'Yüksek basınçlı kamera sistemiyle kanal içi görüntüleme yapıyor, tıkanıklıkları hızla gideriyoruz. Eski atık su sistemlerini modern standartlara taşıyoruz.',
    features: ['Kamera ile Tespit', 'Yüksek Basınçlı Temizlik', 'Kanal Revizyonu', 'Atık Su Sistemleri'],
  },
  {
    icon: '❄️',
    title: 'Isıtma Sistemleri',
    desc: 'Yerden ısıtma, panel radyatör ve kombi sistemleri kurulum & bakımı.',
    detail: 'Enerji verimliliği yüksek ısıtma çözümleri sunuyoruz. Yerden ısıtmadan panel radyatöre, kombiden merkezi ısıtmaya kadar tüm sistemlerde uzman hizmet.',
    features: ['Yerden Isıtma', 'Panel Radyatör', 'Kombi Servisi', 'Merkezi Isıtma'],
  },
  {
    icon: '🛡️',
    title: 'Acil Müdahale',
    desc: '7/24 acil servis. Boru patlaması, su baskını — anında biz varız.',
    detail: 'Gece veya gündüz, hafta sonu veya resmi tatil fark etmez — acil durumlarda ortalama 30 dakikada yerinde oluyoruz. Su baskını, gaz kaçağı, boru patlaması anında müdahale.',
    features: ['7/24 Ulaşılabilirlik', 'Ort. 30 dk Yanıt', 'Su Baskını Müdahale', 'Gaz Kaçağı Acil'],
  },
];

const stats = [
  { value: '15+', label: 'Yıllık Deneyim' },
  { value: '10K+', label: 'Tamamlanan Proje' },
  { value: '100%', label: 'Müşteri Memnuniyeti' },
  { value: '24/7', label: 'Acil Servis' },
];

const gallery = [
  { src: '/images/gallery1.jpg', alt: 'Boru Hattı Yenileme' },
  { src: '/images/gallery2.jpg', alt: 'Pis Su Hattı Revizyonu' },
  { src: '/images/gallery3.jpg', alt: 'Duvar Açma & Tesisat' },
  { src: '/images/gallery4.jpg', alt: 'Mutfak Zemin Onarımı' },
  { src: '/images/gallery5.jpg', alt: 'Kanal & Drenaj Çalışması' },
  { src: '/images/gallery6.jpg', alt: 'Su Sızıntısı Müdahalesi' },
];

const testimonials = [
  {
    name: 'Ahmet Kaya',
    location: 'Kadıköy, İstanbul',
    text: 'Boru patlamasında gece yarısı aradım, 30 dakikada geldiler. Hem hızlı hem uygun fiyatlı. Kesinlikle tavsiye ederim.',
    rating: 5,
  },
  {
    name: 'Fatma Demir',
    location: 'Ataşehir, İstanbul',
    text: 'Banyomuzu baştan aşağı yenilediler. İşçilik kalitesi mükemmel, temiz ve titiz çalıştılar.',
    rating: 5,
  },
  {
    name: 'Mehmet Yılmaz',
    location: 'Beşiktaş, İstanbul',
    text: 'Doğalgaz tesisatını güvenle yaptılar. Lisanslı usta geldi, tüm belgeleri eksiksiz teslim ettiler.',
    rating: 5,
  },
  {
    name: 'Elif Şahin',
    location: 'Sarıyer, İstanbul',
    text: 'Kombi arızasında aynı gün geldiler, sorunu hızlıca çözdüler. Fiyat da beklediğimden çok daha uygundu. Teşekkürler!',
    rating: 5,
  },
  {
    name: 'Burak Arslan',
    location: 'Zekeriyaköy, İstanbul',
    text: 'Mutfak tadilatı sırasında tüm su tesisatını yenilediler. Planlı, düzenli ve çok profesyonel bir ekip.',
    rating: 5,
  },
  {
    name: 'Selin Çelik',
    location: 'Maslak, İstanbul',
    text: 'Dairemizin tıkanıklık sorununu defalarca farklı yerler uğraştı çözemedi. Odak Tesisat tek seferde halletti. Harika!',
    rating: 5,
  },
  {
    name: 'Hasan Öztürk',
    location: 'Levent, İstanbul',
    text: 'Yerden ısıtma sistemimizi kurdu. Hem süreç boyunca hem sonrasında çok ilgili ve yardımsever davrandılar.',
    rating: 5,
  },
  {
    name: 'Ayşe Koç',
    location: 'Tarabya, İstanbul',
    text: 'Su kaçağı olduğunu anlamak bile zordu ama uzman gözle bulup sanki hiç açılmamış gibi kapattılar. Süper iş!',
    rating: 5,
  },
  {
    name: 'Emre Doğan',
    location: 'Büyükdere, İstanbul',
    text: 'Ofisimizin tüm lavabo ve tuvalet tesisatını yenilediler. Kısa sürede, temiz bırakarak bitirdiler. Çok memnunum.',
    rating: 5,
  },
  {
    name: 'Zeynep Aydın',
    location: 'İstinye, İstanbul',
    text: 'Duşakabini kendileri temin edip monte ettiler, çok şık ve sağlam durdu. Fiyat performans açısından ideal.',
    rating: 5,
  },
  {
    name: 'Serkan Bulut',
    location: 'Rumeli Hisarı, İstanbul',
    text: 'Acil su baskını müdahalesi için aradım. İnanılmaz hızlı geldiler, daha fazla hasar oluşmadan durdurdular. Sağ olun.',
    rating: 5,
  },
  {
    name: 'Neslihan Güneş',
    location: 'Eyüpsultan, İstanbul',
    text: 'Banyoyu sıfırdan yeniledik, her detayı sabırla anlattılar ve istediklerimizi eksiksiz uyguladılar. Gerçekten kaliteli.',
    rating: 5,
  },
];

// ── Smooth Scroll ─────────────────────────────────────────────────────────────
function smoothScrollTo(targetY, duration = 900) {
  const startY = window.scrollY;
  const diff = targetY - startY;
  let start = null;

  // Ease in-out cubic
  const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

  function step(ts) {
    if (!start) start = ts;
    const elapsed = ts - start;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo(0, startY + diff * ease(progress));
    if (progress < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

function useSmoothNav() {
  const handleNav = useCallback((e, href) => {
    if (!href.startsWith('#')) return;
    e.preventDefault();
    const target = document.querySelector(href);
    if (!target) return;
    const navHeight = document.querySelector('.navbar')?.offsetHeight || 80;
    const targetY = target.getBoundingClientRect().top + window.scrollY - navHeight;
    smoothScrollTo(targetY, 950);
  }, []);
  return handleNav;
}

// ── Components ────────────────────────────────────────────────────────────────
function Navbar({ scrolled }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState('');
  const handleNav = useSmoothNav();

  const links = [
    { href: '#hero', label: 'Ana Sayfa' },
    { href: '#hizmetler', label: 'Hizmetler' },
    { href: '#hakkimizda', label: 'Hakkımızda' },
    { href: '#galeri', label: 'Galeri' },
    { href: '#yorumlar', label: 'Yorumlar' },
    { href: '#konum', label: 'Konum' },
  ];

  // Track active section on scroll (links is stable — defined outside render)
  useEffect(() => {
    const ids = ['#hero','#hizmetler','#hakkimizda','#galeri','#yorumlar','#konum'];
    const sections = ids.map(id => document.querySelector(id)).filter(Boolean);
    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(e => { if (e.isIntersecting) setActive('#' + e.target.id); });
      },
      { threshold: 0.3 }
    );
    sections.forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const onLinkClick = (e, href) => {
    handleNav(e, href);
    setActive(href);
    setOpen(false);
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="nav-inner">
        <a href="#hero" className="nav-logo" onClick={e => { handleNav(e, '#hero'); setActive(''); }}>
          <span className="logo-icon">⬡</span>
          <span className="logo-text">Odak <em>Tesisat</em></span>
        </a>

        <ul className={`nav-links ${open ? 'open' : ''}`}>
          {links.map(l => (
            <li key={l.href}>
              <a
                href={l.href}
                className={active === l.href ? 'nav-active' : ''}
                onClick={e => onLinkClick(e, l.href)}
              >
                {l.label}
                <span className="nav-underline" />
              </a>
            </li>
          ))}
          <li>
            <a href="tel:+905388410175" className="nav-cta" onClick={() => setOpen(false)}>
              📞 Hemen Ara
            </a>
          </li>
        </ul>

        <button className={`burger ${open ? 'open' : ''}`} onClick={() => setOpen(!open)} aria-label="Menü">
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}

function Hero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <section id="hero" className="hero">
      <CosmicOceanShader />

      <div className={`hero-content ${loaded ? 'loaded' : ''}`}>
        <div className="hero-brand-row">
          <a
            href="https://www.google.com/search?q=Odak+tesisat&stick=H4sIAAAAAAAA_-NgU1IxqDBMTk02M7RMtDQ1NjJKtbQyqDAyNktJTDU1NDIyNTc2tkhaxMrjn5KYrVCSWpxZnFgCACFdiCs3AAAA&hl=tr"
            target="_blank"
            rel="noopener noreferrer"
            className="hero-brand-tag"
          >
            <span className="hero-brand-dot" />
            Odak Tesisat
          </a>
          <span className="hero-brand-sep">✦</span>
          <a
            href="https://www.google.com/search?q=Yal%C4%B1n+tesisat&stick=H4sIAAAAAAAA_-NgU1IxqEi0ME9NNDJOTjQ3MTBJTUuxMqhINjI1STRITDFKTDVOTDVdxMoXmZhzZGOeQklqcWZxYgkA5fAw_jkAAAA&hl=tr"
            target="_blank"
            rel="noopener noreferrer"
            className="hero-brand-tag"
          >
            <span className="hero-brand-dot" />
            Yalın Tesisat
          </a>
        </div>
        <div className="hero-badge">✦ İstanbul'un Güvenilir Tesisatçısı</div>
        <h1 className="hero-title">
          <span className="line1">Profesyonel</span>
          <span className="line2">Tesisat</span>
          <span className="line3">Çözümleri</span>
        </h1>
        <p className="hero-sub">
          15 yılı aşkın deneyimimizle su, doğalgaz ve ısıtma sistemlerinde<br />
          güvenli, hızlı ve kalıcı çözümler sunuyoruz.
        </p>
        <div className="hero-actions">
          <a href="tel:+905388410175" className="btn-primary">Teklif Al</a>
          <a href="tel:+905388410175" className="btn-outline">
            <span className="pulse-dot" /> 7/24 Acil Hat
          </a>
        </div>
        <div className="hero-stats">
          {stats.map(s => (
            <div key={s.label} className="hero-stat">
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Center Contact Card */}
      <div className={`hero-contact-card ${loaded ? 'loaded' : ''}`}>
        <div className="hero-contact-title">
          <span className="hero-contact-label">İletişim</span>
          <strong>Bize Ulaşın</strong>
          <span className="hero-contact-badge">7/24 Açık</span>
        </div>
        <div className="hero-contact-items">
          <a href="tel:+905388410175" className="hero-contact-item">
            <div className="hero-contact-icon-wrap">📞</div>
            <div>
              <small>Telefon</small>
              <strong>+90 538 841 01 75</strong>
            </div>
          </a>
          <a href="https://maps.google.com/?q=Zekeriyakoy+Sarıyer+Istanbul" className="hero-contact-item">
            <div className="hero-contact-icon-wrap">📍</div>
            <div>
              <small>Adres</small>
              <strong>Zekeriyaköy, Sarıyer / İstanbul</strong>
            </div>
          </a>
        </div>
      </div>

      {/* Hero image placeholder */}
      <div className={`hero-visual ${loaded ? 'loaded' : ''}`}>
        <div className="hero-img-wrap">
          <img src="/images/hero.jpg" alt="Odak Tesisat Çalışması" className="hero-img" />
          <div className="hero-img-overlay" />
        </div>
      </div>

      <div className="scroll-hint">
        <span>Aşağı Kaydır</span>
        <div className="scroll-line" />
      </div>
    </section>
  );
}

function Services() {
  const [ref, visible] = useInView();
  const [active, setActive] = useState(0);

  const prev = () => setActive(i => (i - 1 + services.length) % services.length);
  const next = () => setActive(i => (i + 1) % services.length);

  const getPos = (i) => {
    const total = services.length;
    const diff = ((i - active) % total + total) % total;
    if (diff === 0) return 'center';
    if (diff === 1) return 'right1';
    if (diff === 2) return 'right2';
    if (diff === total - 1) return 'left1';
    if (diff === total - 2) return 'left2';
    return 'hidden';
  };

  return (
    <section id="hizmetler" className="services" ref={ref}>
      <div className="container">
        <div className={`section-header ${visible ? 'visible' : ''}`}>
          <span className="section-label">— Hizmetlerimiz</span>
          <h2>Her İhtiyacınıza<br /><em>Eksiksiz Çözüm</em></h2>
          <p>Bir hizmete tıklayarak detayları inceleyin.</p>
        </div>

        <div className={`svc-carousel ${visible ? 'visible' : ''}`}>
          {/* Cards */}
          <div className="svc-stage">
            {services.map((s, i) => {
              const pos = getPos(i);
              return (
                <div
                  key={s.title}
                  className={`svc-card svc-card--${pos}`}
                  onClick={() => setActive(i)}
                >
                  <div className="svc-card-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  {pos === 'center' && <p className="svc-card-desc">{s.desc}</p>}
                </div>
              );
            })}
          </div>

          {/* Detail panel */}
          <div className="svc-detail">
            {services.map((s, i) => (
              <div key={s.title} className={`svc-detail-panel ${i === active ? 'active' : ''}`}>
                <div className="svc-detail-header">
                  <span className="svc-detail-icon">{s.icon}</span>
                  <div>
                    <span className="section-label">— Hizmet Detayı</span>
                    <h3>{s.title}</h3>
                  </div>
                </div>
                <p>{s.detail}</p>
                <ul className="svc-features">
                  {s.features.map(f => (
                    <li key={f}><span className="svc-check">✓</span>{f}</li>
                  ))}
                </ul>
                <a href="tel:+905388410175" className="btn-primary">Bilgi Al</a>
              </div>
            ))}
          </div>

          {/* Navigation */}
          <div className="svc-nav">
            <button className="svc-nav-btn" onClick={prev} aria-label="Önceki">‹</button>
            <div className="svc-dots">
              {services.map((_, i) => (
                <button
                  key={i}
                  className={`svc-dot ${i === active ? 'active' : ''}`}
                  onClick={() => setActive(i)}
                  aria-label={`Hizmet ${i + 1}`}
                />
              ))}
            </div>
            <button className="svc-nav-btn" onClick={next} aria-label="Sonraki">›</button>
          </div>
        </div>
      </div>
    </section>
  );
}

function About() {
  const [ref, visible] = useInView();

  return (
    <section id="hakkimizda" className="about" ref={ref}>
      <div className="about-bg-text">TESISAT</div>
      <div className="container about-inner">
        <div className={`about-visual ${visible ? 'visible' : ''}`}>
          <div className="about-img-stack">
            <img src="/images/about1.jpg" alt="Ekibimiz" className="about-img about-img-main" />
            <img src="/images/about2.jpg" alt="Çalışma" className="about-img about-img-sub" />
            <div className="about-badge">
              <strong>15+</strong>
              <span>Yıllık<br />Deneyim</span>
            </div>
          </div>
        </div>

        <div className={`about-text ${visible ? 'visible' : ''}`}>
          <span className="section-label">— Biz Kimiz</span>
          <h2>İstanbul'da<br /><em>Güvenin Adresi</em></h2>
          <p>
            2009 yılından bu yana Sarıyer ve çevresinde binlerce mutlu müşteriye hizmet veriyoruz.
            Zekeriyaköy merkezli ekibimizle su tesisatından doğalgaz hatlarına,
            ısıtma sistemlerinden acil müdahaleye kadar her konuda yanınızdayız.
          </p>
          <p>
            Kaliteli malzeme, şeffaf fiyatlandırma ve işimize olan sevgimizle
            İstanbul'un kuzeyinde fark yaratan bir marka olduk.
          </p>
          <ul className="about-list">
            {[
              'Lisanslı ve sertifikalı ustalar',
              'Orijinal ve kaliteli malzeme kullanımı',
              'Şeffaf ve dürüst fiyat politikası',
              '2 yıl işçilik garantisi',
            ].map(item => (
              <li key={item}><span className="check">✓</span>{item}</li>
            ))}
          </ul>
          <a href="tel:+905388410175" className="btn-primary">Bize Ulaşın</a>
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  const [ref, visible] = useInView();
  const [active, setActive] = useState(null);

  return (
    <section id="galeri" className="gallery" ref={ref}>
      <div className="container">
        <div className={`section-header ${visible ? 'visible' : ''}`}>
          <span className="section-label">— Çalışmalarımız</span>
          <h2>Tamamlanan<br /><em>Projeler</em></h2>
        </div>

        <div className={`gallery-grid ${visible ? 'visible' : ''}`}>
          {gallery.map((img, i) => (
            <div
              key={img.alt}
              className="gallery-item"
              style={{ '--delay': `${i * 0.08}s` }}
              onClick={() => setActive(img)}
            >
              <img src={img.src} alt={img.alt} />
              <div className="gallery-overlay">
                <span>{img.alt}</span>
                <div className="gallery-zoom">⊕</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {active && (
        <div className="lightbox" onClick={() => setActive(null)}>
          <div className="lightbox-inner" onClick={e => e.stopPropagation()}>
            <img src={active.src} alt={active.alt} />
            <p>{active.alt}</p>
            <button onClick={() => setActive(null)}>✕</button>
          </div>
        </div>
      )}
    </section>
  );
}

function Testimonials() {
  const [ref, visible] = useInView();

  return (
    <section id="yorumlar" className="testimonials" ref={ref}>
      <div className="container">
        <div className={`section-header ${visible ? 'visible' : ''}`}>
          <span className="section-label">— Müşteri Yorumları</span>
          <h2>Onlar Ne<br /><em>Düşünüyor?</em></h2>
        </div>

        <div className={`testimonials-grid ${visible ? 'visible' : ''}`}>
          {testimonials.map((t, i) => (
            <div key={t.name} className="testimonial-card" style={{ '--delay': `${i * 0.15}s` }}>
              <div className="stars">{'★'.repeat(t.rating)}</div>
              <p>"{t.text}"</p>
              <div className="testimonial-author">
                <div className="author-avatar">{t.name[0]}</div>
                <div>
                  <strong>{t.name}</strong>
                  <small>{t.location}</small>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


function Location() {
  const [ref, visible] = useInView();

  return (
    <section id="konum" className="location-section" ref={ref}>
      <div className="container">
        <div className={`section-header ${visible ? 'visible' : ''}`}>
          <span className="section-label">— Bizi Bulun</span>
          <h2>Konum &amp;<br /><em>Google Listelerimiz</em></h2>
        </div>

        <div className={`location-inner ${visible ? 'visible' : ''}`}>
          {/* Map */}
          <div className="location-map-wrap">
            <iframe
              title="Odak Tesisat Konum"
              src="https://maps.google.com/maps?q=Zekeriyak%C3%B6y%2C+1.+Cd.+No%3A6%2C+34260+Sar%C4%B1yer%2F%C4%B0stanbul&output=embed&z=15"
              width="100%"
              height="100%"
              style={{ border: 0, borderRadius: '16px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {/* Google Links */}
          <div className="location-links">
            <p className="location-links-title">Google'da Listelerimiz</p>

            <a
              href="https://www.google.com/search?q=Odak+tesisat&stick=H4sIAAAAAAAA_-NgU1IxqDBMTk02M7RMtDQ1NjJKtbQyqDAyNktJTDU1NDIyNTc2tkhaxMrjn5KYrVCSWpxZnFgCACFdiCs3AAAA&hl=tr"
              target="_blank"
              rel="noopener noreferrer"
              className="location-link-card"
            >
              <div className="location-link-icon">
                <svg viewBox="0 0 48 48" width="28" height="28"><path fill="#4285F4" d="M24 9.5c3.1 0 5.8 1.1 8 2.9l6-6C34.5 3.2 29.6 1 24 1 14.8 1 6.9 6.3 3 14l7 5.4C11.9 13.1 17.5 9.5 24 9.5z"/><path fill="#34A853" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.4c-.5 2.8-2.1 5.2-4.5 6.8l7 5.4c4.1-3.8 6.5-9.4 6.5-16.2z"/><path fill="#FBBC05" d="M10 28.9A14.8 14.8 0 0 1 9.5 24c0-1.7.3-3.3.7-4.9L3.2 13.7A23.8 23.8 0 0 0 .5 24c0 3.8.9 7.4 2.5 10.6L10 28.9z"/><path fill="#EA4335" d="M24 46.5c5.4 0 10-1.8 13.3-4.8l-7-5.4c-1.8 1.2-4.1 1.9-6.6 1.9-6.4 0-11.9-4.3-13.8-10.2L3 33.4C6.9 41.4 14.8 46.5 24 46.5z"/></svg>
              </div>
              <div className="location-link-text">
                <strong>Odak Tesisat</strong>
                <small>Google'da Ara →</small>
              </div>
            </a>

            <a
              href="https://www.google.com/search?q=Yal%C4%B1n+tesisat&stick=H4sIAAAAAAAA_-NgU1IxqEi0ME9NNDJOTjQ3MTBJTUuxMqhINjI1STRITDFKTDVOTDVdxMoXmZhzZGOeQklqcWZxYgkA5fAw_jkAAAA&hl=tr"
              target="_blank"
              rel="noopener noreferrer"
              className="location-link-card"
            >
              <div className="location-link-icon">
                <svg viewBox="0 0 48 48" width="28" height="28"><path fill="#4285F4" d="M24 9.5c3.1 0 5.8 1.1 8 2.9l6-6C34.5 3.2 29.6 1 24 1 14.8 1 6.9 6.3 3 14l7 5.4C11.9 13.1 17.5 9.5 24 9.5z"/><path fill="#34A853" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.4c-.5 2.8-2.1 5.2-4.5 6.8l7 5.4c4.1-3.8 6.5-9.4 6.5-16.2z"/><path fill="#FBBC05" d="M10 28.9A14.8 14.8 0 0 1 9.5 24c0-1.7.3-3.3.7-4.9L3.2 13.7A23.8 23.8 0 0 0 .5 24c0 3.8.9 7.4 2.5 10.6L10 28.9z"/><path fill="#EA4335" d="M24 46.5c5.4 0 10-1.8 13.3-4.8l-7-5.4c-1.8 1.2-4.1 1.9-6.6 1.9-6.4 0-11.9-4.3-13.8-10.2L3 33.4C6.9 41.4 14.8 46.5 24 46.5z"/></svg>
              </div>
              <div className="location-link-text">
                <strong>Yalın Tesisat</strong>
                <small>Google'da Ara →</small>
              </div>
            </a>

          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <div className="container footer-inner">
        <div className="footer-brand">
          <span className="logo-icon">⬡</span>
          <span className="logo-text">Odak <em>Tesisat</em></span>
          <p>Profesyonel tesisat çözümleri ile<br />yanınızdayız.</p>
        </div>
        <div className="footer-links">
          <h4>Hızlı Erişim</h4>
          <a href="#hizmetler">Hizmetler</a>
          <a href="#hakkimizda">Hakkımızda</a>
          <a href="#galeri">Galeri</a>
          <a href="#yorumlar">Yorumlar</a>
        </div>
        <div className="footer-contact">
          <h4>İletişim</h4>
          <a href="tel:+905388410175">+90 538 841 01 75</a>
          <span>Zekeriyaköy, Sarıyer / İstanbul</span>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2025 Odak Tesisat. Tüm hakları saklıdır.</p>
      </div>
    </footer>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <Navbar scrolled={scrolled} />
      <button
        className={`scroll-top-btn ${scrolled ? 'visible' : ''}`}
        onClick={() => smoothScrollTo(0, 800)}
        aria-label="Yukarı çık"
      >
        ↑
      </button>
      <main>
        <Hero />
        <Services />
        <About />
        <Gallery />
        <Testimonials />
        <Location />
      </main>
      <Footer />
    </>
  );
}
